import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from '@/utils/api/axios';
import { IConsumptionBag, IConsumptionBagTransformed, ChoicesStatusConsumptionBag } from '../types';
import { t } from 'i18next';
import { useDecimalFormatter } from '@hooks/index';
import moment from 'moment-timezone';

interface UseConsumptionBagsReturn {
  data: IConsumptionBagTransformed[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Gets the formatted label for a consumption bag
 * @param bag - The consumption bag object
 * @param t - Translation function
 * @returns Formatted label string
 */
const getBagLabel = (bag: IConsumptionBag, formatter: Intl.NumberFormat): string => {
  const balance = formatter.format(bag.currentBalance || 0);
  const expirationDate = bag.expirationDate ? moment(bag.expirationDate).format('L') : '';

  return `${t('Current Balance: $ ')} ${balance} | ${t('Expires')}: ${expirationDate}`;
};

/**
 * Transforms raw consumption bag data into frontend-friendly format
 * @param bags - Array of raw consumption bags
 * @param t - Translation function
 * @returns Array of transformed consumption bags
 */
export const transformConsumptionBags = (
  bags: IConsumptionBag[],
  formatter: Intl.NumberFormat
): IConsumptionBagTransformed[] => {
  return bags.map((bag) => ({
    ...bag,
    name: bag.sku?.name || bag.name || t('Unknown Bag'),
    label: bag.sku?.name || bag.name || t('Unknown Bag'),
    code: getBagLabel(bag, formatter),
    isExpired: bag.expirationDate ? new Date(bag.expirationDate) < new Date() : false,
    isActive: bag.status === ChoicesStatusConsumptionBag.ACTIVE,
    hasBalance: (bag.currentBalance || 0) > 0,
  }));
};

/**
 * Custom hook for fetching and managing consumption bags for an organization
 * Provides filtered views and automatic data transformation
 *
 * @param organizationId - Organization ID to fetch bags for
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Object containing consumption bags data and utility functions
 */
export const useConsumptionBags = (
  organizationId: string,
  enabled: boolean = true
): UseConsumptionBagsReturn => {
  const { i18n } = useTranslation();
  const formatter = useDecimalFormatter(i18n.language);

  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['consumption-bags', organizationId],
    queryFn: async (): Promise<IConsumptionBag[]> => {
      if (!organizationId) {
        return [];
      }

      const filter: any = {
        pageSize: 100,
        organization: {
          _id: organizationId,
        },
      };

      const response = await axios.post('consumption-bags/all', {
        filter,
      });

      return response?.data?.docs || [];
    },
    enabled: enabled && !!organizationId,
    refetchOnMount: 'always',
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const transformedData = useMemo(() => {
    if (!rawData || !Array.isArray(rawData)) {
      return [];
    }

    return transformConsumptionBags(rawData, formatter);
  }, [rawData, formatter]);

  return {
    data: transformedData,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
};

/**
 * Hook for auto-selecting consumption bags when only one is available
 * Useful for forms where automatic selection improves UX
 *
 * @param organizationId - Organization ID to fetch bags for
 * @param autoSelectCallback - Callback function called when auto-selection occurs
 * @returns Available bags and auto-selection status
 */
export const useAutoSelectConsumptionBag = (
  organizationId: string,
  autoSelectCallback?: (bagId: string) => void
) => {
  const { data, isLoading, isError, error, refetch } = useConsumptionBags(organizationId);

  useMemo(() => {
    if (data.length === 1 && autoSelectCallback && !isLoading) {
      const singleBag = data[0];
      autoSelectCallback(singleBag._id);
    }
  }, [data, autoSelectCallback, isLoading]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasSingleBag: data.length === 1,
    hasMultipleBags: data.length > 1,
    hasNoBags: data.length === 0,
  };
};
